import React from 'react';
import { Drawer } from 'antd';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import { IconButton } from 'src/components/design-system/buttons';
import { PandaFriendly } from 'src/images';
import { setAIV2ChatState } from 'shared/store/actions/ai-v2.actions';
import analyticsService from 'src/helpers/analytics.service';
import { ANALYTICS_SOURCES, colorPalette, EVENTS } from 'shared/constants';
import navigationContextManager from 'shared/helpers/src/navigation-context-manager';
import { PANDA_CSS_VARS } from '../panda-css-vars';
import styles from './panda-chat-v2.module.scss';
import { usePandaConversation } from './panda-chat-provider';
import { RenderMessages } from './components/render-messages/render-messages';
import { RenderUserInput } from './components/render-user-input/render-user-input';

/**
 * V2 Panda chat: streaming, multi-source answers. Gated behind the
 * `panda-v2` per-org flag; the existing chat (V1) is used when off.
 * `PandaChatProvider` is mounted by the router above every layout, so fullscreen routes do not end a run.
 */
export function PandaChatV2() {
  const dispatch = useDispatch();
  const { conversationId, lang, isOpen, isChatEnabled, messages, clearChat } =
    usePandaConversation();

  const closeChat = () => {
    dispatch(setAIV2ChatState(false));
    analyticsService.track(EVENTS.AI_CHAT.CLOSE);
  };

  const handleMouseEnter = () => {
    navigationContextManager.setContext({
      source: ANALYTICS_SOURCES.AI_CHAT,
      sourceId: conversationId,
    });
  };

  const handleMouseLeave = () => {
    navigationContextManager.clearContext();
  };

  if (!isChatEnabled) {
    return null;
  }

  return (
    <Drawer
      open={isOpen}
      onClose={closeChat}
      placement="right"
      mask={false}
      closable={false}
      className="new-design-system"
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
        footer: { padding: 0, width: '100%' },
        wrapper: {
          ...PANDA_CSS_VARS,
          width: '40%',
          top: 'var(--header-height)',
          height: 'calc(100% - var(--header-height))',
        },
      }}
      title={
        <div className={styles.header}>
          <div className={styles.brand}>
            <img className={styles.avatar} src={PandaFriendly} alt="" />
            <h2>{lang.PANDA_HEADER || 'Ask Panda'}</h2>
          </div>
          <div className={styles.headerActions}>
            {messages.length > 0 && (
              <button type="button" className={styles.clearChat} onClick={clearChat}>
                {lang.CLEAR_CHAT_MOBILE || lang.CLEAR_CHAT}
              </button>
            )}
            <IconButton
              tooltipProps={{ placement: 'left' }}
              type="button"
              onClick={closeChat}
              label={lang.CLOSE_CHAT || 'Close chat'}>
              <FontAwesomeIcon icon={faXmark} color={colorPalette.primaryMain8} size="lg" />
            </IconButton>
          </div>
        </div>
      }
      footer={<RenderUserInput />}>
      <div
        className={styles.bodyWrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <RenderMessages />
      </div>
    </Drawer>
  );
}
